import React, { useState, useEffect } from 'react';
import { apiRequest } from '../api';

const EditIntegrationConfigModal = ({ onClose, onSave, integration, errors, setErrors, isSubmitting = false }) => {
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
    const [coreFieldMappingsText, setCoreFieldMappingsText] = useState('');
    const [fieldMappingsText, setFieldMappingsText] = useState('');
    const [formData, setFormData] = useState({
        configuration: integration?.configuration || {}
    });
    const apiUrl = process.env.REACT_APP_API_BASE_URL;

    // Fetch template when modal opens
    useEffect(() => {
        if (integration?.template_id) {
            fetchTemplate();
        }
    }, [integration?.template_id]);

    // Initialize text areas when template is loaded
    useEffect(() => {
        if (selectedTemplate && integration?.configuration) {
            const fieldMappings = integration.configuration.field_mappings || {};
            const coreMappings = fieldMappings.core_field_mappings || {};
            const fieldMappingsObj = fieldMappings.field_mappings || {};
            
            setCoreFieldMappingsText(Object.entries(coreMappings)
                .map(([key, value]) => `${key}:${value}`)
                .join('\n'));
            
            setFieldMappingsText(Object.entries(fieldMappingsObj)
                .map(([key, value]) => `${key}:${value}`)
                .join('\n'));
        }
    }, [selectedTemplate, integration?.configuration]);

    const fetchTemplate = async () => {
        setIsLoadingTemplate(true);
        try {
            const token = localStorage.getItem('token');
            const response = await apiRequest(`${apiUrl}/integration-templates/${integration.template_id}`, 'GET', null, token);
            
            if (response && response.id) {
                setSelectedTemplate(response);
            }
        } catch (error) {
            console.error('Error fetching template:', error);
        } finally {
            setIsLoadingTemplate(false);
        }
    };

    const updateConfiguration = (section, field, value) => {
        setFormData(prev => ({
            ...prev,
            configuration: {
                ...prev.configuration,
                [section]: {
                    ...prev.configuration[section],
                    [field]: value
                }
            }
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Call the parent's onSave function
        onSave(formData);
    };

    const renderAuthenticationFields = () => {
        if (!selectedTemplate || !selectedTemplate.configuration?.authentication) return null;
        const authConfig = selectedTemplate.configuration.authentication;
        const authType = authConfig.type;
        const fields = authConfig.fields || [];

        if (authType === 'none') {
            return (
                <div style={{ marginBottom: '10px', color: '#666', fontStyle: 'italic' }}>
                    No authentication required for this integration.
                </div>
            );
        }

        return (
            <div style={{ marginBottom: '15px' }}>
                <h4 style={{ marginBottom: '10px', fontSize: '14px' }}>Authentication</h4>
                {fields.map((field) => (
                    <div key={field} style={{ marginBottom: '10px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                            {field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </label>
                        <input
                            type={field.toLowerCase().includes('password') ? 'password' : 'text'}
                            value={formData.configuration.authentication?.[field] || ''}
                            onChange={(e) => updateConfiguration('authentication', field, e.target.value)}
                            placeholder={field.replace(/_/g, ' ')}
                            style={{
                                width: '100%',
                                padding: '8px',
                                border: '1px solid #ddd',
                                borderRadius: '4px'
                            }}
                        />
                    </div>
                ))}
                {authType === 'api_key' && (
                    <div style={{ marginBottom: '10px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                            Header Name
                        </label>
                        <input
                            type="text"
                            value={formData.configuration.authentication?.header_name || 'X-API-Key'}
                            onChange={(e) => updateConfiguration('authentication', 'header_name', e.target.value)}
                            placeholder="X-API-Key"
                            style={{
                                width: '100%',
                                padding: '8px',
                                border: '1px solid #ddd',
                                borderRadius: '4px'
                            }}
                        />
                    </div>
                )}
                {authType === 'oauth2' && (
                    <>
                        <div style={{ marginBottom: '10px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                                Token URL
                            </label>
                            <input
                                type="url"
                                value={formData.configuration.authentication?.token_url || ''}
                                onChange={(e) => updateConfiguration('authentication', 'token_url', e.target.value)}
                                placeholder="https://oauth.example.com/token"
                                style={{
                                    width: '100%',
                                    padding: '8px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px'
                                }}
                            />
                        </div>
                        <div style={{ marginBottom: '10px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                                Scope
                            </label>
                            <input
                                type="text"
                                value={formData.configuration.authentication?.scope || ''}
                                onChange={(e) => updateConfiguration('authentication', 'scope', e.target.value)}
                                placeholder="read write"
                                style={{
                                    width: '100%',
                                    padding: '8px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px'
                                }}
                            />
                        </div>
                    </>
                )}
            </div>
        );
    };

    const renderFieldMappingConfig = () => {
        if (!selectedTemplate || !selectedTemplate.configuration?.field_mappings) return null;
        const fieldMappings = selectedTemplate.configuration.field_mappings;
        const mappingType = formData.configuration.field_mappings?.type || fieldMappings.type;

        return (
            <div style={{ marginBottom: '20px', padding: '15px', border: '1px solid #ddd', borderRadius: '5px' }}>
                <h4 style={{ marginBottom: '15px', fontSize: '16px' }}>
                    Field Mapping Configuration
                    <span style={{ marginLeft: '8px', cursor: 'help', color: '#666', fontSize: '14px' }}>ℹ</span>
                </h4>
                
                <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                        Mapping Type
                        <span style={{ marginLeft: '4px', cursor: 'help', color: '#666', fontSize: '12px' }}>ℹ</span>
                    </label>
                    <select
                        value={mappingType}
                        onChange={(e) => updateConfiguration('field_mappings', 'type', e.target.value)}
                        style={{
                            width: '100%',
                            padding: '8px',
                            border: '1px solid #ddd',
                            borderRadius: '4px'
                        }}
                    >
                        <option value="direct_match">Direct Match</option>
                        <option value="core_plus_extra">Core + Extra</option>
                        <option value="field_name_mapping">Field Name Mapping</option>
                        <option value="core_plus_extra_mapped">Core + Extra with Field Mapping</option>
                    </select>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                        Choose how form fields are mapped to CRM fields
                    </div>
                </div>

                {mappingType === 'direct_match' && (
                    <div>
                        <div style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                                Required Fields
                                <span style={{ marginLeft: '4px', cursor: 'help', color: '#666', fontSize: '12px' }}>ℹ</span>
                            </label>
                            <input
                                type="text"
                                value={formData.configuration.field_mappings?.required_fields?.join(', ') || fieldMappings.required_fields?.join(', ') || ''}
                                onChange={(e) => updateConfiguration('field_mappings', 'required_fields', e.target.value.split(',').map(f => f.trim()).filter(f => f))}
                                placeholder="First Name, Last Name, Email (comma-separated)"
                                style={{
                                    width: '100%',
                                    padding: '8px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px'
                                }}
                            />
                            <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                                Form fields that must be present (comma-separated)
                            </div>
                        </div>
                        <div style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                                Optional Fields
                                <span style={{ marginLeft: '4px', cursor: 'help', color: '#666', fontSize: '12px' }}>ℹ</span>
                            </label>
                            <input
                                type="text"
                                value={formData.configuration.field_mappings?.optional_fields?.join(', ') || fieldMappings.optional_fields?.join(', ') || ''}
                                onChange={(e) => updateConfiguration('field_mappings', 'optional_fields', e.target.value.split(',').map(f => f.trim()).filter(f => f))}
                                placeholder="Phone, Address, Notes (comma-separated)"
                                style={{
                                    width: '100%',
                                    padding: '8px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px'
                                }}
                            />
                            <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                                Form fields that may be present (comma-separated)
                            </div>
                        </div>
                    </div>
                )}

                {mappingType === 'core_plus_extra' && (
                    <div>
                        <div style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                                Core Fields
                                <span style={{ marginLeft: '4px', cursor: 'help', color: '#666', fontSize: '12px' }}>ℹ</span>
                            </label>
                            <input
                                type="text"
                                value={formData.configuration.field_mappings?.core_fields?.join(', ') || fieldMappings.core_fields?.join(', ') || ''}
                                onChange={(e) => updateConfiguration('field_mappings', 'core_fields', e.target.value.split(',').map(f => f.trim()).filter(f => f))}
                                placeholder="First Name, Last Name, Email (comma-separated)"
                                style={{
                                    width: '100%',
                                    padding: '8px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px'
                                }}
                            />
                            <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                                Fields that will be sent directly to CRM (comma-separated)
                            </div>
                        </div>
                        <div style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                                Extra Field Name
                                <span style={{ marginLeft: '4px', cursor: 'help', color: '#666', fontSize: '12px' }}>ℹ</span>
                            </label>
                            <input
                                type="text"
                                value={formData.configuration.field_mappings?.extra_field_name || fieldMappings.extra_field_name || ''}
                                onChange={(e) => updateConfiguration('field_mappings', 'extra_field_name', e.target.value)}
                                placeholder="extra_args"
                                style={{
                                    width: '100%',
                                    padding: '8px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px'
                                }}
                            />
                            <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                                Name of the field that will contain all non-core fields
                            </div>
                        </div>
                    </div>
                )}

                {mappingType === 'core_plus_extra_mapped' && (
                    <div>
                        <div style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                                Core Field Mappings
                                <span style={{ marginLeft: '4px', cursor: 'help', color: '#666', fontSize: '12px' }}>ℹ</span>
                            </label>
                            <textarea
                                value={coreFieldMappingsText}
                                onChange={(e) => {
                                    const rawText = e.target.value;
                                    setCoreFieldMappingsText(rawText);
                                    
                                    // Parse valid mappings and update form data
                                    const mappings = {};
                                    const lines = rawText.split('\n');
                                    lines.forEach(line => {
                                        const trimmedLine = line.trim();
                                        if (trimmedLine && trimmedLine.includes(':')) {
                                            const colonIndex = trimmedLine.indexOf(':');
                                            if (colonIndex > 0) {
                                                const key = trimmedLine.substring(0, colonIndex).trim();
                                                const value = trimmedLine.substring(colonIndex + 1).trim();
                                                if (key && value) {
                                                    mappings[key] = value;
                                                }
                                            }
                                        }
                                    });
                                    
                                    updateConfiguration('field_mappings', 'core_field_mappings', mappings);
                                }}
                                placeholder="First Name:first_name\nLast Name:last_name\nEmail:email_address"
                                rows={5}
                                style={{
                                    width: '100%',
                                    padding: '8px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    fontFamily: 'monospace',
                                    fontSize: '12px'
                                }}
                            />
                            <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                                Map core form fields to specific CRM field names (form_field:crm_field). Add new lines to create additional mappings.
                            </div>
                            <button
                                type="button"
                                onClick={() => {
                                    const templateMappings = selectedTemplate.configuration.field_mappings.core_field_mappings || {};
                                    updateConfiguration('field_mappings', 'core_field_mappings', templateMappings);
                                    
                                    // Update the text area with template mappings
                                    setCoreFieldMappingsText(Object.entries(templateMappings)
                                        .map(([key, value]) => `${key}:${value}`)
                                        .join('\n'));
                                }}
                                style={{
                                    marginTop: '5px',
                                    padding: '4px 8px',
                                    fontSize: '11px',
                                    backgroundColor: '#f8f9fa',
                                    border: '1px solid #ddd',
                                    borderRadius: '3px',
                                    cursor: 'pointer'
                                }}
                            >
                                Reset to Template Defaults
                            </button>
                        </div>
                        <div style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                                Extra Field Name
                                <span style={{ marginLeft: '4px', cursor: 'help', color: '#666', fontSize: '12px' }}>ℹ</span>
                            </label>
                            <input
                                type="text"
                                value={formData.configuration.field_mappings?.extra_field_name || fieldMappings.extra_field_name || ''}
                                onChange={(e) => updateConfiguration('field_mappings', 'extra_field_name', e.target.value)}
                                placeholder="extra_args"
                                style={{
                                    width: '100%',
                                    padding: '8px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px'
                                }}
                            />
                            <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                                Name of the field that will contain all non-core fields
                            </div>
                        </div>
                    </div>
                )}

                {mappingType === 'field_name_mapping' && (
                    <div>
                        <div style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                                Field Mappings
                                <span style={{ marginLeft: '4px', cursor: 'help', color: '#666', fontSize: '12px' }}>ℹ</span>
                            </label>
                            <textarea
                                value={fieldMappingsText}
                                onChange={(e) => {
                                    const rawText = e.target.value;
                                    setFieldMappingsText(rawText);
                                    
                                    // Parse valid mappings and update form data
                                    const mappings = {};
                                    const lines = rawText.split('\n');
                                    lines.forEach(line => {
                                        const trimmedLine = line.trim();
                                        if (trimmedLine && trimmedLine.includes(':')) {
                                            const colonIndex = trimmedLine.indexOf(':');
                                            if (colonIndex > 0) {
                                                const key = trimmedLine.substring(0, colonIndex).trim();
                                                const value = trimmedLine.substring(colonIndex + 1).trim();
                                                if (key && value) {
                                                    mappings[key] = value;
                                                }
                                            }
                                        }
                                    });
                                    
                                    updateConfiguration('field_mappings', 'field_mappings', mappings);
                                }}
                                placeholder="First Name:first_name\nLast Name:last_name\nEmail:email_address"
                                rows={5}
                                style={{
                                    width: '100%',
                                    padding: '8px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    fontFamily: 'monospace',
                                    fontSize: '12px'
                                }}
                            />
                            <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                                Map form field names to CRM field names (form_field:crm_field). Add new lines to create additional mappings.
                            </div>
                            <button
                                type="button"
                                onClick={() => {
                                    const templateMappings = selectedTemplate.configuration.field_mappings.field_mappings || {};
                                    updateConfiguration('field_mappings', 'field_mappings', templateMappings);
                                    
                                    // Update the text area with template mappings
                                    setFieldMappingsText(Object.entries(templateMappings)
                                        .map(([key, value]) => `${key}:${value}`)
                                        .join('\n'));
                                }}
                                style={{
                                    marginTop: '5px',
                                    padding: '4px 8px',
                                    fontSize: '11px',
                                    backgroundColor: '#f8f9fa',
                                    border: '1px solid #ddd',
                                    borderRadius: '3px',
                                    cursor: 'pointer'
                                }}
                            >
                                Reset to Template Defaults
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    if (isLoadingTemplate) {
        return (
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 1000
            }}>
                <div style={{
                    backgroundColor: '#fff',
                    padding: '20px',
                    borderRadius: '8px',
                    textAlign: 'center'
                }}>
                    <div style={{ fontSize: '24px', marginBottom: '10px' }}>⏳</div>
                    <div>Loading template configuration...</div>
                </div>
            </div>
        );
    }

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
        }}>
            <div style={{
                backgroundColor: '#fff',
                padding: '20px',
                borderRadius: '8px',
                width: '90%',
                maxWidth: '800px',
                maxHeight: '90vh',
                overflow: 'auto'
            }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '20px'
                }}>
                    <h2 style={{ fontSize: '24px', margin: 0 }}>Edit Configuration - {integration?.name}</h2>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '24px',
                            cursor: 'pointer',
                            padding: '5px'
                        }}
                    >
                        ×
                    </button>
                </div>

                <form onSubmit={handleSubmit} style={{ position: 'relative' }}>
                    {isSubmitting && (
                        <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(255, 255, 255, 0.8)',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            zIndex: 10,
                            borderRadius: '8px'
                        }}>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '24px', marginBottom: '10px' }}>⏳</div>
                                <div>Updating Configuration...</div>
                            </div>
                        </div>
                    )}

                    {selectedTemplate && (
                        <div style={{ marginTop: '20px', padding: '15px', border: '1px solid #ddd', borderRadius: '5px' }}>
                            <h3 style={{ marginBottom: '15px', fontSize: '16px' }}>Template Configuration</h3>
                            
                            {/* Endpoints */}
                            {selectedTemplate.configuration?.endpoints && (
                                <div style={{ marginBottom: '15px' }}>
                                    <h4 style={{ marginBottom: '10px', fontSize: '14px' }}>Endpoints</h4>
                                    <div style={{ marginBottom: '10px' }}>
                                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                                            Base URL
                                        </label>
                                        <input
                                            type="url"
                                            value={formData.configuration.endpoints?.base_url || ''}
                                            onChange={(e) => updateConfiguration('endpoints', 'base_url', e.target.value)}
                                            placeholder="https://api.example.com"
                                            style={{
                                                width: '100%',
                                                padding: '8px',
                                                border: '1px solid #ddd',
                                                borderRadius: '4px'
                                            }}
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Authentication */}
                            {renderAuthenticationFields()}

                            {/* Field Mapping Configuration */}
                            {renderFieldMappingConfig()}
                        </div>
                    )}

                    <div style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: '10px',
                        marginTop: '20px'
                    }}>
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={isSubmitting}
                            style={{
                                padding: '10px 20px',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                backgroundColor: '#f8f9fa',
                                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                opacity: isSubmitting ? 0.6 : 1
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            style={{
                                padding: '10px 20px',
                                border: 'none',
                                borderRadius: '4px',
                                backgroundColor: isSubmitting ? '#6c757d' : '#007bff',
                                color: 'white',
                                cursor: isSubmitting ? 'not-allowed' : 'pointer'
                            }}
                        >
                            {isSubmitting ? (
                                <>
                                    <span style={{ marginRight: '8px' }}>⏳</span>
                                    Updating...
                                </>
                            ) : (
                                'Update Configuration'
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditIntegrationConfigModal; 